import { useState } from 'react';
import { useToast } from '@crisellevaldez/speculo-ui';
import { connectCINC, getCINCStatus } from '../../api/cinc';
import { updateOrganization } from '../../api/remiApi';

export const useCINCOperations = () => {
  // Loading states
  const [isConnecting, setIsConnecting] = useState(false);
  const [isFetchingStatus, setIsFetchingStatus] = useState(false);

  const { showToast } = useToast();

  /**
   * Connect to CINC CRM by opening OAuth popup
   */
  const connectToCINC = async (selectedOrgId: string): Promise<boolean> => {
    setIsConnecting(true);
    try {
      await connectCINC(selectedOrgId);
      await updateOrganization(selectedOrgId, {
        cincSetup: { connected: true, connectedAt: new Date().toISOString() },
      });
      showToast({
        message: 'CINC integration connected successfully',
        variant: 'success',
      });
      return true;
    } catch (error) {
      console.error('Error connecting to CINC:', error);
      const e = error as { response?: { data?: { error?: { message?: string } } }; message?: string };
      let errorMessage = e?.response?.data?.error?.message || e?.message || 'Failed to connect to CINC';

      if (errorMessage.includes('Popup blocked')) {
        errorMessage = 'Popup blocked. Please allow popups for this site and try again.';
      } else if (errorMessage.includes('cancelled')) {
        errorMessage = 'CINC authorization was cancelled';
      }

      showToast({
        message: errorMessage,
        variant: 'error',
      });
      return false;
    } finally {
      setIsConnecting(false);
    }
  };

  /**
   * Check CINC connection status
   */
  const checkCINCStatus = async (selectedOrgId: string) => {
    setIsFetchingStatus(true);
    try {
      const status = await getCINCStatus(selectedOrgId);
      return status;
    } catch (error) {
      console.error('Error checking CINC status:', error);
      const e = error as { response?: { data?: { error?: { message?: string } } }; message?: string };
      const errorMessage = e?.response?.data?.error?.message || e?.message || 'Failed to check CINC status';

      showToast({
        message: errorMessage,
        variant: 'error',
      });
      return null;
    } finally {
      setIsFetchingStatus(false);
    }
  };

  return {
    // Connection operations
    connectToCINC,
    isConnecting,

    // Status operations
    checkCINCStatus,
    isFetchingStatus,
  };
};
